import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronDown, ChevronUp, HelpCircle, ArrowRight } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          question: '¿Cuánto cuesta instalar placas solares?',
          answer: 'El coste de una instalación solar varía según el tamaño del sistema y tus necesidades energéticas. Una instalación residencial típica oscila entre 4.000€ y 8.000€. Contamos con opciones de financiación sin entrada inicial y te ayudamos a tramitar todas las subvenciones disponibles, que pueden reducir el coste hasta un 40%. Lo importante es que la inversión se amortiza típicamente en 1-3 años gracias al ahorro en tu factura eléctrica.'
        },
        {
          question: '¿Cuánto puedo ahorrar en mi factura eléctrica?',
          answer: 'El ahorro promedio es del 70% en tu factura eléctrica. Dependiendo de tu consumo, orientación del tejado y tamaño del sistema, puedes reducir tu factura entre 50€ y 150€ mensuales. Una familia media con un consumo de 300 kWh mensuales puede ahorrar unos 1.200€ al año. El ahorro acumulado en 25 años puede superar los 16.500€, sin contar el aumento del valor de tu vivienda.'
        },
        {
          question: '¿Cuánto tiempo dura una instalación solar?',
          answer: 'Los paneles solares tienen una garantía de fabricante de 25 años y una vida útil superior a 30 años. Tras 25 años, los paneles suelen mantener al menos el 80% de su eficiencia original. Los inversores tienen una vida útil de 10-15 años y pueden ser reemplazados fácilmente. Además, ofrecemos garantía de instalación de 10 años y servicio de mantenimiento para asegurar el óptimo rendimiento de tu sistema.'
        }
      ]
    },
    {
      category: 'Instalación',
      questions: [
        {
          question: '¿Cuánto tiempo tarda la instalación?',
          answer: 'La instalación física de los paneles solares se completa en 1-2 días. El proceso completo desde la consulta inicial hasta la activación del sistema suele tardar 2 semanas, incluyendo el estudio previo, los trámites administrativos y la puesta en marcha. Una vez activado el sistema, empiezas a generar energía y ahorrar desde el primer día.'
        },
        {
          question: '¿Qué trámites son necesarios?',
          answer: 'Para instalar paneles solares necesitas varios permisos: licencia municipal de obras, autorización de conexión a red de la distribuidora eléctrica y registro en industria. Además, si quieres beneficiarte de subvenciones, hay que tramitar las solicitudes correspondientes. No te preocupes, en Nubia Solar nos encargamos de todos los trámites administrativos de forma gratuita. Tú solo tienes que firmar la documentación necesaria.'
        },
        {
          question: '¿Necesito reformar mi tejado?',
          answer: 'En la mayoría de los casos no es necesario reformar el tejado. Realizamos una inspección previa para verificar que la estructura es adecuada para soportar el peso de los paneles (aproximadamente 15 kg/m²). Si tu tejado está en buenas condiciones y tiene menos de 30 años, normalmente no se requieren obras adicionales. Solo en casos excepcionales de tejados muy deteriorados o con problemas estructurales sería necesario realizar reparaciones previas.'
        },
        {
          question: '¿Puedo instalar paneles si mi tejado tiene sombras?',
          answer: 'Las sombras reducen la eficiencia del sistema, pero no lo hacen inviable. Realizamos un estudio de sombreado para evaluar el impacto y diseñar el sistema de forma óptima. Con optimizadores de potencia o microinversores, podemos minimizar el efecto de las sombras. Si tu tejado tiene sombras significativas, podemos sugerir alternativas como instalar los paneles en otra ubicación (pérgola, garaje, suelo) o utilizar sistemas de seguimiento solar.'
        }
      ]
    },
    {
      category: 'Subvenciones y Financiación',
      questions: [
        {
          question: '¿Hay subvenciones disponibles?',
          answer: 'Sí, existen múltiples ayudas para instalaciones solares: Subvenciones autonómicas de hasta 600€/kWp (pueden cubrir hasta el 40% del coste), bonificaciones del IBI del 50% durante varios años, bonificaciones del ICIO de hasta el 95%, y deducciones en el IRPF. Las ayudas varían según la comunidad autónoma y el ayuntamiento. En Nubia Solar te asesoramos sobre todas las ayudas disponibles en tu zona y nos encargamos gratuitamente de tramitarlas.'
        },
        {
          question: '¿Ofrecen financiación?',
          answer: 'Sí, colaboramos con entidades financieras para ofrecer condiciones ventajosas de financiación. Puedes instalar tu sistema solar sin entrada inicial y pagar la instalación con el ahorro que generas en tu factura eléctrica. De esta forma, desde el primer mes tu cuota de financiación es inferior al ahorro que obtienes, por lo que sale rentable desde el día uno. Una vez amortizada la instalación, todo el ahorro es para ti durante más de 25 años.'
        },
        {
          question: '¿Cuál es el plazo de amortización?',
          answer: 'Con los precios actuales de la electricidad y las subvenciones disponibles, el plazo de amortización típico es de 1 a 3 años. Esto significa que recuperas tu inversión en ese periodo. A partir de entonces, disfrutas de ahorro puro durante más de 25 años. El plazo exacto depende de varios factores: tamaño de la instalación, tu consumo eléctrico, subvenciones obtenidas y condiciones de financiación.'
        }
      ]
    },
    {
      category: 'Funcionamiento',
      questions: [
        {
          question: '¿Qué pasa si no hay sol?',
          answer: 'Los paneles solares generan electricidad incluso en días nublados, aunque con menor eficiencia (aproximadamente 10-25% de su capacidad). Cuando no hay suficiente producción solar, tu instalación automáticamente toma electricidad de la red eléctrica. Si produces más energía de la que consumes, puedes verter el excedente a la red y recibir compensación económica, o almacenarlo en baterías para usarlo cuando lo necesites.'
        },
        {
          question: '¿Necesito baterías?',
          answer: 'Las baterías no son imprescindibles para una instalación solar. La mayoría de instalaciones en España funcionan conectadas a la red eléctrica (sin baterías), lo que permite consumir energía de la red cuando no produces suficiente y verter los excedentes. Las baterías son recomendables si quieres maximizar tu autoconsumo, tener energía de respaldo en caso de cortes de suministro, o si vives en una zona aislada sin acceso a la red eléctrica.'
        },
        {
          question: '¿Qué mantenimiento requieren los paneles?',
          answer: 'Los paneles solares requieren muy poco mantenimiento. Es recomendable limpiarlos 1-2 veces al año para eliminar polvo, hojas o excrementos de aves que puedan reducir su eficiencia. La lluvia suele mantenerlos relativamente limpios. Además, conviene revisar anualmente que no haya daños físicos y que el sistema funcione correctamente. Ofrecemos contratos de mantenimiento opcionales donde nos encargamos de todas estas tareas.'
        },
        {
          question: '¿Funciona para viviendas y empresas?',
          answer: 'Sí, la energía solar es perfecta tanto para viviendas como para empresas. En viviendas unifamiliares, adosados y comunidades de vecinos, permite reducir drásticamente la factura eléctrica. En empresas, oficinas, naves industriales y comercios, supone un ahorro significativo en los costes operativos y mejora la imagen corporativa al demostrar compromiso con la sostenibilidad. Diseñamos soluciones específicas para cada tipo de cliente.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-nubia-gold-soft/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <HelpCircle className="w-10 h-10 text-nubia-gold-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">
              Preguntas Frecuentes
            </h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Resolvemos todas tus dudas sobre energía solar e instalaciones fotovoltaicas
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-12">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="text-3xl font-bold text-nubia-green-primary mb-6 pb-3 border-b-2 border-nubia-gold-primary/30">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((faq, questionIndex) => {
                    const key = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openIndex === key;
                    
                    return (
                      <Card 
                        key={questionIndex} 
                        className="border-2 border-nubia-gray-light hover:border-nubia-gold-primary/30 transition-all cursor-pointer"
                        onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                      >
                        <CardHeader>
                          <CardTitle className="text-xl text-nubia-green-primary flex items-start justify-between gap-4">
                            <span>{faq.question}</span>
                            {isOpen ? (
                              <ChevronUp className="w-6 h-6 text-nubia-gold-primary flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-6 h-6 text-nubia-gold-primary flex-shrink-0" />
                            )}
                          </CardTitle>
                        </CardHeader>
                        {isOpen && (
                          <CardContent>
                            <p className="text-nubia-text-dark/70 leading-relaxed text-lg">{faq.answer}</p>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Help */}
      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-nubia-green-primary mb-6">
            ¿No Encuentras Tu Respuesta?
          </h2>
          <p className="text-xl text-nubia-text-dark/70 mb-10 max-w-3xl mx-auto">
            Nuestro equipo de expertos está disponible para resolver cualquier duda que tengas
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contacto">
              <Button size="lg" className="bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-7 text-lg shadow-xl">
                Contactar con Nosotros
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-nubia-green-primary text-nubia-green-primary hover:bg-nubia-green-primary hover:text-white px-10 py-7 text-lg"
              onClick={() => window.location.href = 'tel:+34604921745'}
            >
              Llamar Ahora
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;